import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Card, CardHeader, CardBody, Heading, Text, Spinner, VStack } from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { WaybillForm } from '@/components/forms/WaybillForm'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

export default function WaybillCreatePage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { bookingId } = useParams<{ bookingId: string }>()
  const { showError } = useToastNotification()
  const [validatedBookingId, setValidatedBookingId] = useState<number | null>(null)
  const [bookingData, setBookingData] = useState<any>(null)
  const [isValidating, setIsValidating] = useState(true)

  useEffect(() => {
    if (!bookingId) {
      navigate('/bookings', { replace: true })
      return
    }

    const parsedId = Number(bookingId)
    if (Number.isNaN(parsedId)) {
      navigate('/bookings', { replace: true })
      return
    }

    const fetchBookingData = async () => {
      try {
        const { data } = await apiClient.get(`/bookings/${parsedId}`)
        const booking = data?.data || data || {}
        setBookingData(booking)
        setValidatedBookingId(parsedId)
      } catch (error: any) {
        showError(error)
        navigate('/bookings', { replace: true })
      } finally {
        setIsValidating(false)
      }
    }

    fetchBookingData()
  }, [bookingId, navigate, showError])

  const handleSuccess = () => {
    if (bookingId) {
      navigate(`/bookings/waybills/${bookingId}`)
    } else {
      navigate('/bookings')
    }
  }

  const handleCancel = () => {
    if (bookingId) {
      navigate(`/bookings/waybills/${bookingId}`)
    } else {
      navigate('/bookings')
    }
  }

  if (isValidating || validatedBookingId === null || !bookingData) {
    return (
      <AdminLayout>
        <Box p={4}>
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Add Waybill</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Create a new waybill for this booking
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0}>
            <WaybillForm
              bookingId={validatedBookingId}
              bookingReferenceNumber={bookingData.reference_number}
              shippingLineId={bookingData.shipping_line_id}
              shippingLineName={bookingData.shipping_line?.name}
              cypaFromName={bookingData.cypa_from?.name}
              cypaToName={bookingData.cypa_to?.name}
              cypaToId={bookingData.cypa_id_to}
              cypaFromId={bookingData.cypa_id_from}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </CardBody>
        </Card>
      </Box>
    </AdminLayout>
  )
}

